/* eslint-disable react/no-unstable-nested-components, react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Package, Check, Clock, MessageSquarePlus, Hourglass, XCircle, CheckCircle2, PackageMinus, PackageCheck, PackagePlus, AlertTriangle, Building2 } from "lucide-react";
import TimeInput24 from "./TimeInput24";
import { toast } from "sonner";
import SignaturePad from "./SignaturePad";
import EquipmentReturnTimeline from "./EquipmentReturnTimeline";
import SiteReceptionsToConfirm from "./SiteReceptionsToConfirm";
import SitePickupsToSign from "./SitePickupsToSign";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { formatDateFr } from "../lib/dateFormat";

const ETAT_COLOR = {
  "Neuf": "bg-emerald-50 border-emerald-200 text-emerald-700",
  "Bon état": "bg-sky-50 border-sky-200 text-sky-700",
  "État d'usage": "bg-amber-50 border-amber-200 text-amber-700",
  "Endommagé": "bg-red-50 border-red-200 text-red-700",
};

const REASON_OPTIONS = [
  { value: "usure",  label: "Usure" },
  { value: "defaut", label: "Défaut constaté" },
  { value: "perte",  label: "Perte" },
  { value: "autre",  label: "Autre" },
];

const REQ_STATUS = {
  en_attente: { label: "En attente", icon: Hourglass, cls: "bg-amber-50 border-amber-200 text-amber-700" },
  approuve:   { label: "Approuvée",  icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  refuse:     { label: "Refusée",    icon: XCircle, cls: "bg-red-50 border-red-200 text-red-700" },
};

const ETAT_OPTIONS = [
  { value: "bon",          label: "Bon état" },
  { value: "use",          label: "Usé" },
  { value: "endommage",    label: "Endommagé" },
  { value: "inutilisable", label: "Inutilisable" },
];

const RETURN_ETAT_LABELS = {
  bon: "Bon état",
  use: "Usé",
  endommage: "Endommagé",
  inutilisable: "Inutilisable",
  perte: "Inutilisable", // legacy
};

const RETURN_ETAT_CLS = {
  bon:          "bg-emerald-50 border-emerald-200 text-emerald-700",
  use:          "bg-amber-50 border-amber-200 text-amber-700",
  endommage:    "bg-red-50 border-red-200 text-red-700",
  inutilisable: "bg-zinc-100 border-zinc-300 text-zinc-700",
  perte:        "bg-zinc-100 border-zinc-300 text-zinc-700",
};

export default function MesEquipements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [signature, setSignature] = useState(null);

  // Demandes de matériel — dialog à 2 onglets (Remplacement / Nouveau besoin)
  const [requests, setRequests] = useState([]);
  const [openRequest, setOpenRequest] = useState(false);
  const [reqTab, setReqTab] = useState("remplacement");
  const [reqAssignmentId, setReqAssignmentId] = useState("");
  const [reqReasonType, setReqReasonType] = useState("usure");
  const [reqReason, setReqReason] = useState("");
  const [reqQty, setReqQty] = useState(1);
  const [reqBesoinTitre, setReqBesoinTitre] = useState("");
  const [reqBesoinDesc, setReqBesoinDesc] = useState("");
  const [reqSuggestionUrl, setReqSuggestionUrl] = useState("");
  const [reqBrandId, setReqBrandId] = useState("");
  const [reqBrands, setReqBrands] = useState([]);
  const [reqSignature, setReqSignature] = useState(null);
  const [reqControllerSignature, setReqControllerSignature] = useState(null);
  const [reqAutoValidate, setReqAutoValidate] = useState(false);
  const [reqSubmitting, setReqSubmitting] = useState(false);

  // Demandes de restitution reçues par le collaborateur
  const [returns, setReturns] = useState([]);
  const [returnDialog, setReturnDialog] = useState(null); // demande sélectionnée
  const [returnForm, setReturnForm] = useState({
    actual_date: "",
    actual_time: "",
    etat: "bon",
    etat_note: "",
    collab_signature: null,
  });

  const load = async () => {
    if (!user?.id) return;
    try {
      // « Mon profil » : tous les appels doivent être strictement scopés à
      // l'utilisateur courant — sinon un admin/manager consultant son espace
      // personnel verrait les demandes/restitutions des autres collaborateurs.
      const [a, r, rt] = await Promise.all([
        api.get("/equipment-collab-assignments/mine"),
        api.get("/equipment-requests", { params: { requester_id: user.id } }),
        api.get("/equipment-returns", { params: { collaborateur_id: user.id } }),
      ]);
      setItems(a.data || []);
      setRequests(r.data || []);
      setReturns(rt.data || []);
    } catch {
      toast.error("Erreur chargement");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  // Rafraîchissement temps réel (notifications matériel & restitution)
  useEffect(() => {
    const onNotif = (ev) => {
      const t = ev?.detail?.type;
      if (!t) return;
      if (
        t === "equipment_request" || t === "equipment_request_decision"
        || t === "equipment_return_requested" || t === "equipment_return_cancelled"
        || t === "equipment_return_confirmed" || t === "equipment_return_validated"
        || t === "equipment_return_rejected_by_controller"
      ) load();
    };
    window.addEventListener("plania:notification", onNotif);
    return () => window.removeEventListener("plania:notification", onNotif);
  }, []);

  const openConfirm = (a) => {
    setCurrent(a);
    setSignature(null);
    setOpen(true);
  };

  const submitConfirm = async () => {
    if (!signature) {
      toast.error("Veuillez signer pour confirmer la réception");
      return;
    }
    try {
      await api.put(`/equipment-collab-assignments/${current.id}/confirm`, { collab_signature: signature });
      toast.success("Réception confirmée");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const openCreateRequest = async () => {
    setReqTab("remplacement");
    setReqAssignmentId("");
    setReqReasonType("usure");
    setReqReason("");
    setReqQty(1);
    setReqBesoinTitre("");
    setReqBesoinDesc("");
    setReqSuggestionUrl("");
    setReqBrandId("");
    setReqSignature(null);
    setReqControllerSignature(null);
    setReqAutoValidate(false);
    setOpenRequest(true);
    // Charge les départements accessibles (utile pour "nouveau besoin")
    try {
      const r = await api.get("/brands");
      setReqBrands(r.data || []);
    } catch {
      setReqBrands([]);
    }
  };

  const submitRequest = async () => {
    // Validations communes
    if (!reqSignature) {
      toast.error("Signature du demandeur obligatoire");
      return;
    }
    const qty = Number(reqQty);
    if (!qty || qty < 1) {
      toast.error("Quantité minimale : 1");
      return;
    }
    const isAdmin = user?.role === "admin";
    if (isAdmin && reqAutoValidate && !reqControllerSignature) {
      toast.error("Signature contrôleur obligatoire pour l'auto-validation");
      return;
    }
    const payload = {
      request_type: reqTab,
      quantite_demandee: qty,
      requester_signature: reqSignature,
    };
    if (reqTab === "remplacement") {
      if (!reqAssignmentId) {
        toast.error("Veuillez sélectionner le matériel à remplacer");
        return;
      }
      if (!REASON_OPTIONS.find((r) => r.value === reqReasonType)) {
        toast.error("Motif invalide");
        return;
      }
      payload.assignment_id = reqAssignmentId;
      payload.reason_type = reqReasonType;
      payload.reason = reqReason;
    } else {
      if (!reqBesoinTitre.trim()) {
        toast.error("Désignation du besoin obligatoire");
        return;
      }
      if (!reqBrandId) {
        toast.error("Département obligatoire pour un nouveau besoin");
        return;
      }
      payload.besoin_titre = reqBesoinTitre.trim();
      payload.besoin_description = reqBesoinDesc;
      payload.suggestion_url = reqSuggestionUrl;
      payload.brand_id = reqBrandId;
    }
    if (isAdmin && reqAutoValidate) {
      payload.controller_signature = reqControllerSignature;
    }
    try {
      setReqSubmitting(true);
      await api.post("/equipment-requests", payload);
      toast.success(
        isAdmin && reqAutoValidate
          ? "Demande enregistrée et auto-validée"
          : "Demande envoyée pour validation",
      );
      setOpenRequest(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setReqSubmitting(false);
    }
  };

  const cancelRequest = async (id) => {
    if (!window.confirm("Annuler cette demande ?")) return;
    try {
      await api.delete(`/equipment-requests/${id}`);
      toast.success("Demande annulée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  // ---- Confirmation de restitution ----
  const openReturnDialog = (r) => {
    const now = new Date();
    // Date/heure en heure LOCALE (pas UTC) pour éviter le décalage de jour
    const pad = (n) => String(n).padStart(2, "0");
    const localDate = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
    const localTime = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setReturnForm({
      actual_date: localDate,
      actual_time: localTime,
      etat: "bon",
      etat_note: "",
      collab_signature: null,
    });
    setReturnDialog(r);
  };

  const submitReturnConfirm = async () => {
    if (!returnDialog) return;
    if (!returnForm.actual_date) return toast.error("Indiquez la date de restitution.");
    if (!returnForm.actual_time) return toast.error("Indiquez l'heure de restitution.");
    if (!returnForm.etat) return toast.error("Précisez l'état du matériel.");
    if (!returnForm.collab_signature) return toast.error("Votre signature est obligatoire.");
    try {
      await api.put(`/equipment-returns/${returnDialog.id}/confirm`, returnForm);
      toast.success("Restitution signée — en attente de contre-signature du contrôleur.");
      setReturnDialog(null);
      load();
      window.dispatchEvent(new CustomEvent("plania:equipment-returns-updated"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la confirmation");
    }
  };

  const pendingReturns = returns.filter((r) => r.status === "pending");
  const awaitingControllerReturns = returns.filter((r) => r.status === "awaiting_controller");
  const confirmedReturns = returns.filter((r) => r.status === "confirmed");

  // Affectations actives (toutes celles que le collab a encore "à charge")
  const activeItems = items.filter((i) => i.status !== "returned");
  // Le matériel signé par le collab mais en attente de contre-signature contrôleur
  // forme désormais un état distinct (miroir inversé de l'affectation).
  const awaitingControllerItems = activeItems
    .filter((i) => i.status === "awaiting_return_controller")
    .map((i) => {
      const ret = awaitingControllerReturns.find((r) => r.assignment_id === i.id);
      return { ...i, _return: ret || null };
    });
  // Affectations encore à confirmer par le collab (réception initiale)
  const pending = activeItems.filter(
    (i) => i.status !== "confirmed" && i.status !== "awaiting_return_controller",
  );
  // Affectations confirmées et toujours sous la responsabilité du collab
  const confirmed = activeItems.filter((i) => i.status === "confirmed");
  // Affectations restituées (matériels que le collab n'a plus en sa possession)
  const restituted = items
    .filter((i) => i.status === "returned")
    .map((i) => {
      const ret = confirmedReturns.find((r) => r.assignment_id === i.id);
      return { ...i, _return: ret || null };
    })
    .sort((a, b) => {
      const da = a.returned_at || a._return?.confirmed_at || "";
      const db = b.returned_at || b._return?.confirmed_at || "";
      return db.localeCompare(da);
    });
  const eligibleForRequest = confirmed; // seules les affectations confirmées peuvent être remplacées

  const Card = ({ a }) => {
    const cls = ETAT_COLOR[a.etat] || "bg-zinc-50 border-zinc-200 text-zinc-700";
    return (
      <div className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid={`my-equip-${a.id}`}>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-sm bg-sky-50 border border-sky-200 flex items-center justify-center flex-shrink-0">
            <Package size={18} className="text-sky-700" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-display text-base font-semibold text-zinc-900 truncate">{a.designation_snapshot}</p>
            <p className="text-xs font-mono text-zinc-500 truncate">Réf. {a.ref_id_snapshot}</p>
            {a.caracteristiques_snapshot && (
              <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{a.caracteristiques_snapshot}</p>
            )}
          </div>
          <span className={`text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider ${cls}`}>
            {a.etat}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs font-mono pt-2 border-t border-zinc-100">
          <div>
            <p className="label-uppercase mb-0.5">Quantité</p>
            <p className="font-semibold text-zinc-900">{a.quantite}</p>
          </div>
          <div>
            <p className="label-uppercase mb-0.5">Date</p>
            <p className="text-zinc-700">{formatDateFr(a.date_affectation)}</p>
          </div>
          <div className="col-span-2">
            <p className="label-uppercase mb-0.5">Remis par</p>
            <p className="text-zinc-700">{a.controller_name || "—"}</p>
          </div>
        </div>
        <div className="pt-2 border-t border-zinc-100">
          {a.status === "confirmed" ? (
            <div className="flex items-center gap-2 text-xs text-emerald-700">
              <Check size={14} />
              <span>Confirmé le {a.confirmed_at ? formatDateFr(a.confirmed_at.slice(0, 10)) : ""}</span>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={() => openConfirm(a)}
              className="bg-[#0055FF] hover:bg-[#0044CC] w-full"
              data-testid={`confirm-receipt-btn-${a.id}`}
            >
              <Check size={12} className="mr-1" /> Confirmer la réception
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="mes-equipements">
      {/* Réceptions matériel sur site en attente — admin/manager désigne le receiver */}
      <SiteReceptionsToConfirm onChanged={load} />

      {/* Enlèvements matériel sur site à signer — admin/manager désigne le référent */}
      <SitePickupsToSign onChanged={load} />

      {/* Header avec bouton demande de matériel */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="label-uppercase mb-1">Vos équipements</p>
          <p className="text-xs text-zinc-500 font-mono">
            Confirmez la réception de vos matériels et signalez tout besoin de remplacement.
          </p>
        </div>
        <Button
          onClick={openCreateRequest}
          className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm"
          data-testid="open-equip-request-btn"
          title="Demande de remplacement ou nouveau besoin"
        >
          <MessageSquarePlus size={14} className="mr-1.5" /> Demande de matériel
        </Button>
      </div>

      {pending.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Clock size={14} className="text-amber-600" />
            <h3 className="font-display text-base font-semibold text-zinc-900">À confirmer ({pending.length})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pending.map((a) => <Card key={a.id} a={a} />)}
          </div>
        </div>
      )}

      {confirmed.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Check size={14} className="text-emerald-600" />
            <h3 className="font-display text-base font-semibold text-zinc-900">Confirmés ({confirmed.length})</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {confirmed.map((a) => <Card key={a.id} a={a} />)}
          </div>
        </div>
      )}

      {items.length === 0 && (
        <div className="text-center py-12 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
          Aucun matériel mis à votre disposition pour le moment.
        </div>
      )}

      {items.length > 0 && activeItems.length === 0 && (
        <div className="text-center py-8 text-sm text-zinc-500 font-mono bg-white border border-zinc-200 rounded-sm">
          Vous n'avez plus de matériel actif. Consultez la section « Équipements et matériels restitués » ci-dessous.
        </div>
      )}

      {/* Restitutions à effectuer */}
      {pendingReturns.length > 0 && (
        <div data-testid="my-equip-returns-pending">
          <div className="flex items-center gap-2 mb-3">
            <PackageMinus size={14} className="text-amber-600" />
            <h3 className="font-display text-base font-semibold text-zinc-900">
              À restituer ({pendingReturns.length})
            </h3>
          </div>
          <div className="bg-white border border-amber-200 rounded-sm divide-y divide-zinc-100">
            {pendingReturns.map((r) => (
              <div
                key={r.id}
                className="p-4 flex items-start gap-3 flex-wrap"
                data-testid={`my-equip-return-${r.id}`}
              >
                <div className="h-10 w-10 rounded-sm bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0">
                  <PackageMinus size={18} className="text-amber-700" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display text-base font-semibold text-zinc-900">
                    {r.designation_snapshot}
                    <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot})</span>
                  </p>
                  <p className="text-xs text-zinc-700 mt-1">
                    <b>Date prévue :</b> {formatDateFr(r.return_date)}
                    <span className="text-zinc-500"> · Créneau : <b>{r.return_start_time} → {r.return_end_time}</b></span>
                  </p>
                  {r.note && (
                    <p className="text-xs text-zinc-600 mt-1 italic">« {r.note} »</p>
                  )}
                  <p className="text-[11px] font-mono text-zinc-400 mt-1">
                    Demandée par {r.requested_by_name || "—"}
                  </p>
                  {r.last_rejection_reason && (
                    <div
                      className="mt-2 bg-red-50 border border-red-200 rounded-sm p-2 text-xs"
                      data-testid={`my-equip-return-rejection-${r.id}`}
                    >
                      <p className="font-semibold text-red-700 mb-0.5">
                        Restitution refusée par {r.last_rejection_by_name || "le contrôleur"}
                        {r.last_rejection_at ? ` le ${formatDateFr(r.last_rejection_at.slice(0, 10))}` : ""}
                      </p>
                      <p className="text-red-800">Motif : « {r.last_rejection_reason} »</p>
                      <p className="text-[11px] text-red-600 mt-1 font-mono">
                        Merci de re-confirmer la restitution avec les corrections appropriées.
                      </p>
                    </div>
                  )}
                </div>
                <Button
                  type="button"
                  onClick={() => openReturnDialog(r)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid={`my-equip-return-confirm-btn-${r.id}`}
                >
                  <Check size={13} className="mr-1.5" /> Confirmer restitution
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Restitutions en attente de contre-signature du contrôleur */}
      {awaitingControllerReturns.length > 0 && (
        <div data-testid="my-equip-returns-awaiting-controller">
          <div className="flex items-center gap-2 mb-3">
            <Hourglass size={14} className="text-sky-600" />
            <h3 className="font-display text-base font-semibold text-zinc-900">
              En attente de contre-signature ({awaitingControllerReturns.length})
            </h3>
          </div>
          <div className="bg-white border border-sky-200 rounded-sm divide-y divide-zinc-100">
            {awaitingControllerReturns.map((r) => (
              <div
                key={r.id}
                className="p-4 space-y-3"
                data-testid={`my-equip-return-awaiting-${r.id}`}
              >
                <div className="flex items-start gap-3 flex-wrap">
                  <div className="h-10 w-10 rounded-sm bg-sky-50 border border-sky-200 flex items-center justify-center flex-shrink-0">
                    <Hourglass size={18} className="text-sky-700" />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-display text-base font-semibold text-zinc-900">
                      {r.designation_snapshot}
                      <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot})</span>
                    </p>
                    <p className="text-xs text-zinc-700 mt-1">
                      Vous avez signé le <b>{r.actual_date ? formatDateFr(r.actual_date) : "—"}</b>
                      {r.actual_time ? <> à <b>{r.actual_time}</b></> : null}
                      {" · État déclaré : "}<b>{RETURN_ETAT_LABELS[r.etat] || r.etat || "—"}</b>
                    </p>
                    <p className="text-[11px] font-mono text-sky-700 mt-1">
                      En attente de contre-signature du contrôleur (admin / manager).
                    </p>
                  </div>
                  <span className="text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider bg-sky-50 border-sky-200 text-sky-700">
                    À contre-signer
                  </span>
                </div>
                <div className="pl-13 sm:pl-13">
                  <EquipmentReturnTimeline
                    request={r}
                    testidPrefix={`return-timeline-collab-awaiting-${r.id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Équipements et matériels restitués (historique) */}
      {restituted.length > 0 && (
        <div data-testid="my-equip-restituted">
          <div className="flex items-center gap-2 mb-3">
            <PackageCheck size={14} className="text-emerald-600" />
            <h3 className="font-display text-base font-semibold text-zinc-900">
              Équipements et matériels restitués ({restituted.length})
            </h3>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            {restituted.map((a) => {
              const ret = a._return;
              const etatKey = (ret?.etat) || a.return_etat || "bon";
              const etatLabel = RETURN_ETAT_LABELS[etatKey] || etatKey;
              const etatCls = RETURN_ETAT_CLS[etatKey] || "bg-zinc-50 border-zinc-200 text-zinc-700";
              const dateRest = ret?.actual_date || (a.returned_at ? a.returned_at.slice(0, 10) : null);
              const heureRest = ret?.actual_time || null;
              const controlleur = ret?.controller_name || ret?.requested_by_name || "—";
              return (
                <div
                  key={a.id}
                  className="p-4 space-y-3"
                  data-testid={`my-equip-restituted-${a.id}`}
                >
                  <div className="flex items-start gap-3 flex-wrap">
                    <div className="h-10 w-10 rounded-sm bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                      <PackageCheck size={18} className="text-emerald-700" />
                    </div>
                    <div className="flex-1 min-w-[220px]">
                      <p className="font-display text-base font-semibold text-zinc-900">
                        {a.designation_snapshot}
                        <span className="text-xs font-mono text-zinc-500 ml-2">({a.ref_id_snapshot})</span>
                      </p>
                      {a.caracteristiques_snapshot && (
                        <p className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{a.caracteristiques_snapshot}</p>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 mt-2 text-xs font-mono">
                        <div>
                          <p className="label-uppercase mb-0.5">Quantité</p>
                          <p className="text-zinc-900 font-semibold">{a.quantite}</p>
                        </div>
                        <div>
                          <p className="label-uppercase mb-0.5">Restitué le</p>
                          <p className="text-zinc-700">
                            {dateRest ? formatDateFr(dateRest) : "—"}
                            {heureRest ? <span className="text-zinc-500"> · {heureRest}</span> : null}
                          </p>
                        </div>
                        <div>
                          <p className="label-uppercase mb-0.5">Contrôleur</p>
                          <p className="text-zinc-700 truncate" title={controlleur}>{controlleur}</p>
                        </div>
                        <div>
                          <p className="label-uppercase mb-0.5">Affecté le</p>
                          <p className="text-zinc-700">{formatDateFr(a.date_affectation)}</p>
                        </div>
                      </div>
                      {ret?.etat_note && (
                        <p className="text-xs text-zinc-600 mt-2 italic bg-zinc-50 border border-zinc-200 rounded-sm px-2 py-1">
                          « {ret.etat_note} »
                        </p>
                      )}
                    </div>
                    <span
                      className={`text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider whitespace-nowrap ${etatCls}`}
                      data-testid={`my-equip-restituted-etat-${a.id}`}
                    >
                      {etatLabel}
                    </span>
                  </div>
                  {ret && (
                    <EquipmentReturnTimeline
                      request={ret}
                      testidPrefix={`return-timeline-collab-restituted-${a.id}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Historique des demandes de matériel */}
      {requests.length > 0 && (
        <div data-testid="my-equip-requests">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquarePlus size={14} className="text-[#0055FF]" />
            <h3 className="font-display text-base font-semibold text-zinc-900">
              Mes demandes de remplacement ({requests.length})
            </h3>
          </div>
          <div className="bg-white border border-zinc-200 rounded-sm divide-y divide-zinc-100">
            {requests.map((r) => {
              const meta = REQ_STATUS[r.status] || REQ_STATUS.en_attente;
              const Icon = meta.icon;
              const reasonLabel = (REASON_OPTIONS.find((o) => o.value === r.reason_type) || {}).label || r.reason_type;
              return (
                <div key={r.id} className="p-3 flex items-start justify-between gap-3" data-testid={`equip-req-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-zinc-900 truncate">
                      {r.designation_snapshot || "—"}
                      <span className="text-xs font-mono text-zinc-500 ml-2">({r.ref_id_snapshot || "—"})</span>
                    </p>
                    <p className="text-xs text-zinc-600 mt-0.5">
                      Motif : <b>{reasonLabel}</b>
                      {r.quantite_demandee ? <span className="text-zinc-500"> · Qté demandée : <b>{r.quantite_demandee}</b></span> : null}
                      {r.quantite_validee && r.quantite_validee !== r.quantite_demandee ? (
                        <span className="text-emerald-700"> · Qté validée : <b>{r.quantite_validee}</b></span>
                      ) : null}
                      {r.reason ? <span className="text-zinc-500"> — {r.reason}</span> : null}
                    </p>
                    <p className="text-[11px] font-mono text-zinc-500 mt-0.5">
                      Demandé le {formatDateFr(r.created_at?.slice(0, 10))}
                      {r.decided_at ? ` · Décision ${formatDateFr(r.decided_at.slice(0, 10))}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-1 border rounded-sm tracking-wider ${meta.cls}`}>
                      <Icon size={11} /> {meta.label}
                    </span>
                    {r.status === "en_attente" && (
                      <Button
                        variant="outline" size="sm" className="h-7 px-2 text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => cancelRequest(r.id)}
                        data-testid={`cancel-equip-req-${r.id}`}
                        title="Annuler la demande"
                      >
                        <XCircle size={12} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal Confirmation de réception */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex flex-col max-h-[90vh] p-0 sm:max-w-md">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Confirmer la réception</DialogTitle>
            <DialogDescription className="sr-only">
              Apposez votre signature pour confirmer la réception du matériel.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
            {current && (
              <>
                <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm space-y-1">
                  <p><b>{current.designation_snapshot}</b> ({current.ref_id_snapshot})</p>
                  <p className="text-xs font-mono text-zinc-600">{current.caracteristiques_snapshot || ""}</p>
                  <p className="text-xs font-mono text-zinc-600">État : {current.etat} · Quantité : {current.quantite}</p>
                </div>
                <p className="text-sm text-zinc-700">
                  En signant ci-dessous, vous confirmez avoir reçu le matériel listé ci-dessus dans l'état indiqué.
                </p>
                <SignaturePad onSave={setSignature} />
              </>
            )}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={submitConfirm} className="bg-[#0055FF] hover:bg-[#0044CC]" data-testid="submit-confirm-receipt">
              <Check size={12} className="mr-1" /> Confirmer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Demande de matériel — 2 onglets : Remplacement / Nouveau besoin */}
      <Dialog open={openRequest} onOpenChange={setOpenRequest}>
        <DialogContent className="flex flex-col max-h-[92vh] p-0 sm:max-w-xl" data-testid="equip-request-dialog">
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Demande de matériel</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500 font-mono">
              Émettez une demande de remplacement d'un matériel existant, ou exprimez un nouveau besoin.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
            <Tabs value={reqTab} onValueChange={setReqTab} className="w-full">
              <TabsList className="grid grid-cols-2 w-full" data-testid="equip-req-tabs">
                <TabsTrigger value="remplacement" data-testid="equip-req-tab-remplacement" className="min-w-0">
                  <PackageMinus size={12} className="mr-1.5 flex-shrink-0" />
                  <span className="truncate">
                    <span className="sm:hidden">Remplacement</span>
                    <span className="hidden sm:inline">Demande de remplacement</span>
                  </span>
                </TabsTrigger>
                <TabsTrigger value="nouveau_besoin" data-testid="equip-req-tab-nouveau" className="min-w-0">
                  <PackagePlus size={12} className="mr-1.5 flex-shrink-0" />
                  <span className="truncate">Nouveau besoin</span>
                </TabsTrigger>
              </TabsList>

              {/* Onglet Remplacement */}
              <TabsContent value="remplacement" className="mt-4 space-y-4">
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Matériel à remplacer <span className="text-red-600">*</span>
                  </Label>
                  <Select
                    value={reqAssignmentId}
                    onValueChange={setReqAssignmentId}
                  >
                    <SelectTrigger data-testid="equip-req-assignment">
                      <SelectValue placeholder={
                        eligibleForRequest.length === 0
                          ? "Aucun matériel affecté à votre profil"
                          : "Sélectionnez le matériel à remplacer…"
                      } />
                    </SelectTrigger>
                    <SelectContent>
                      {eligibleForRequest.length === 0 && (
                        <SelectItem value="__empty" disabled>Aucun matériel affecté</SelectItem>
                      )}
                      {eligibleForRequest.map((a) => (
                        <SelectItem key={a.id} value={a.id} data-testid={`equip-req-assignment-opt-${a.id}`}>
                          {a.designation_snapshot} · {a.ref_id_snapshot} (qté {a.quantite})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-zinc-500 font-mono mt-1">
                    Seuls les matériels dont vous avez confirmé la réception peuvent être remplacés.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Motif <span className="text-red-600">*</span>
                    </Label>
                    <Select value={reqReasonType} onValueChange={setReqReasonType}>
                      <SelectTrigger data-testid="equip-req-reason-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {REASON_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Quantité <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="number" min="1"
                      value={reqQty}
                      onChange={(e) => setReqQty(e.target.value)}
                      data-testid="equip-req-quantite"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Détails (optionnel)</Label>
                  <Textarea
                    rows={3}
                    value={reqReason}
                    onChange={(e) => setReqReason(e.target.value)}
                    placeholder="Décrivez brièvement l'état du matériel ou les circonstances…"
                    data-testid="equip-req-reason"
                  />
                </div>
              </TabsContent>

              {/* Onglet Nouveau besoin */}
              <TabsContent value="nouveau_besoin" className="mt-4 space-y-4">
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Désignation du besoin <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    value={reqBesoinTitre}
                    onChange={(e) => setReqBesoinTitre(e.target.value)}
                    placeholder="Ex : nouveau casque anti-bruit, chariot de manutention…"
                    data-testid="equip-req-besoin-titre"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Département concerné <span className="text-red-600">*</span>
                    </Label>
                    <Select value={reqBrandId} onValueChange={setReqBrandId}>
                      <SelectTrigger data-testid="equip-req-brand">
                        <SelectValue placeholder="Sélectionnez le département…" />
                      </SelectTrigger>
                      <SelectContent>
                        {reqBrands.length === 0 && (
                          <SelectItem value="__nobrand" disabled>Aucun département disponible</SelectItem>
                        )}
                        {reqBrands.map((b) => (
                          <SelectItem key={b.id} value={b.id} data-testid={`equip-req-brand-opt-${b.id}`}>
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 size={12} /> {b.nom || b.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Quantité souhaitée <span className="text-red-600">*</span>
                    </Label>
                    <Input
                      type="number" min="1"
                      value={reqQty}
                      onChange={(e) => setReqQty(e.target.value)}
                      data-testid="equip-req-besoin-qty"
                    />
                  </div>
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">Description du besoin</Label>
                  <Textarea
                    rows={3}
                    value={reqBesoinDesc}
                    onChange={(e) => setReqBesoinDesc(e.target.value)}
                    placeholder="Précisez l'usage, les caractéristiques attendues, l'urgence…"
                    data-testid="equip-req-besoin-desc"
                  />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Lien de suggestion (optionnel)
                  </Label>
                  <Input
                    type="url"
                    value={reqSuggestionUrl}
                    onChange={(e) => setReqSuggestionUrl(e.target.value)}
                    placeholder="https://…"
                    data-testid="equip-req-suggestion-url"
                  />
                </div>
              </TabsContent>
            </Tabs>

            {/* Signature demandeur — obligatoire pour tous */}
            <div className="border-t border-zinc-100 pt-4 mt-4">
              <Label className="label-uppercase mb-1.5 block">
                Signature du demandeur <span className="text-red-600">*</span>
              </Label>
              <p className="text-[11px] text-zinc-500 font-mono mb-2">
                Votre signature valide et engage la demande. Elle sera archivée pour traçabilité.
              </p>
              <SignaturePad onSave={setReqSignature} data-testid="equip-req-signature" />
            </div>

            {/* Bloc Admin — auto-validation */}
            {user?.role === "admin" && (
              <div className="border border-emerald-200 bg-emerald-50/40 rounded-sm p-3 space-y-3" data-testid="equip-req-admin-block">
                <label className="flex items-start gap-2 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={reqAutoValidate}
                    onChange={(e) => setReqAutoValidate(e.target.checked)}
                    data-testid="equip-req-auto-validate"
                  />
                  <span>
                    <b>Auto-valider ma demande</b> — En tant qu'administrateur, cochez pour valider immédiatement.
                    Votre signature contrôleur (ci-dessous) est obligatoire pour cette action.
                  </span>
                </label>
                {reqAutoValidate && (
                  <div>
                    <Label className="label-uppercase mb-1.5 block">
                      Signature contrôleur <span className="text-red-600">*</span>
                    </Label>
                    <SignaturePad onSave={setReqControllerSignature} data-testid="equip-req-controller-signature" />
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-zinc-500 font-mono">
              {user?.role === "manager"
                ? "Votre demande sera transmise aux administrateurs des départements concernés pour validation."
                : "Votre demande sera transmise aux managers du département et aux administrateurs pour validation."}
            </p>
          </div>
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setOpenRequest(false)}>Annuler</Button>
            <Button
              onClick={submitRequest}
              disabled={
                reqSubmitting ||
                !reqSignature ||
                (reqTab === "remplacement" && !reqAssignmentId) ||
                (reqTab === "nouveau_besoin" && (!reqBesoinTitre.trim() || !reqBrandId)) ||
                (user?.role === "admin" && reqAutoValidate && !reqControllerSignature)
              }
              className="bg-[#0055FF] hover:bg-[#0044CC]"
              data-testid="submit-equip-request-btn"
            >
              <MessageSquarePlus size={12} className="mr-1" />
              {user?.role === "admin" && reqAutoValidate
                ? "Enregistrer & auto-valider"
                : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale Confirmation de restitution (collab) — double signature */}
      <Dialog
        open={!!returnDialog}
        onOpenChange={(v) => { if (!v) setReturnDialog(null); }}
      >
        <DialogContent
          className="flex flex-col max-h-[90vh] p-0 sm:max-w-lg"
          data-testid="equip-return-confirm-dialog"
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b border-zinc-100">
            <DialogTitle>Confirmer la restitution du matériel</DialogTitle>
            <DialogDescription className="sr-only">
              Indiquez la date et l'heure de restitution, l'état du matériel, et signez.
            </DialogDescription>
          </DialogHeader>
          {returnDialog && (
            <div className="overflow-y-auto px-6 py-4 flex-1 space-y-4">
              <div className="bg-zinc-50 border border-zinc-200 rounded-sm p-3 text-sm space-y-1">
                <p>
                  <b>{returnDialog.designation_snapshot || "—"}</b>
                  <span className="text-xs font-mono text-zinc-600 ml-2">({returnDialog.ref_id_snapshot || "—"})</span>
                </p>
                <p className="text-xs font-mono text-zinc-600">
                  Demandée par : {returnDialog.requested_by_name || "—"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Date effective <span className="text-red-600">*</span>
                  </Label>
                  <Input
                    type="date"
                    value={returnForm.actual_date}
                    onChange={(e) => setReturnForm({ ...returnForm, actual_date: e.target.value })}
                    data-testid="equip-return-confirm-date"
                  />
                </div>
                <div>
                  <Label className="label-uppercase mb-1.5 block">
                    Heure effective <span className="text-red-600">*</span>
                  </Label>
                  <TimeInput24
                    value={returnForm.actual_time}
                    onChange={(v) => setReturnForm({ ...returnForm, actual_time: v })}
                    data-testid="equip-return-confirm-time"
                  />
                </div>
              </div>

              <div>
                <Label className="label-uppercase mb-1.5 block">
                  État du matériel <span className="text-red-600">*</span>
                </Label>
                <Select
                  value={returnForm.etat}
                  onValueChange={(v) => setReturnForm({ ...returnForm, etat: v })}
                >
                  <SelectTrigger data-testid="equip-return-confirm-etat">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ETAT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Note état <span className="text-zinc-400 normal-case">(optionnel)</span>
                </Label>
                <Textarea
                  rows={2}
                  value={returnForm.etat_note}
                  onChange={(e) => setReturnForm({ ...returnForm, etat_note: e.target.value })}
                  placeholder="Précisions sur l'état (rayures, choc, accessoires manquants…)"
                  data-testid="equip-return-confirm-note"
                />
              </div>

              <div>
                <Label className="label-uppercase mb-1.5 block">
                  Votre signature (collaborateur) <span className="text-red-600">*</span>
                </Label>
                <SignaturePad
                  value={returnForm.collab_signature}
                  onSave={(url) => setReturnForm((f) => ({ ...f, collab_signature: url }))}
                />
                <p className="text-[11px] text-zinc-500 font-mono mt-1 inline-flex items-center gap-1">
                  <AlertTriangle size={10} /> Après votre signature, la restitution sera transmise au contrôleur (admin/manager) pour validation définitive.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="px-6 py-4 border-t border-zinc-100 bg-white">
            <Button variant="outline" onClick={() => setReturnDialog(null)}>Annuler</Button>
            <Button
              onClick={submitReturnConfirm}
              disabled={!returnForm.collab_signature}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="equip-return-confirm-submit"
            >
              <Check size={13} className="mr-1.5" /> Signer la restitution
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
